import { useCallback } from 'react';
import { useStore } from 'react-redux';
import type { RootState } from '@src/core/store';
import { useWidgetId } from '@src/core/IdProvider';
import { getSequentialOpenBlock } from '../core/getSequentialOpenBlock';
import { getOrderedEmbedSlots } from '../core/getOrderedEmbedSlots';
import { useProductBookingSequential } from './useProductBookingSequential';
import { useSlotBlockTitle } from './useSlotBlockTitle';
import { ProductBookingSlot } from '../types/productBookingSlot';
import { trackStepOpened } from '@src/domains/products/features/product-adder-v2/analytics/widgetTracking';

export const useGuardedSequentialOpenChange = (slot: ProductBookingSlot | null, setOpen: (next: boolean) => void) => {
  const widgetId = useWidgetId();
  const store = useStore<RootState>();
  const seq = useProductBookingSequential();
  const slotBlockTitle = useSlotBlockTitle();

  return useCallback(
    (next: boolean) => {
      if (!next) {
        setOpen(false);
        return;
      }

      if (!slot) {
        setOpen(true);
        seq.clearSequentialUi();
        return;
      }

      const state = store.getState();
      const block = getSequentialOpenBlock(widgetId, slot, state);

      if (block) {
        seq.notifySequentialFeedback(slot, block.highlightSlot, slotBlockTitle(block.blockingSlot));

        return;
      }

      // This hook only guards opening a selector — fill vs edit is tracked in each step's onConfirm.
      const productId = state.product.adderState[widgetId]?.productId ?? '';
      const embedSlots = getOrderedEmbedSlots(widgetId, state);
      trackStepOpened(widgetId, productId, slot, embedSlots.indexOf(slot));

      setOpen(true);
      seq.clearSequentialUi();
    },
    [slot, setOpen, seq, widgetId, store, slotBlockTitle],
  );
};
